/* ============================================
   WORK THEME CONFIGURATIONS
   The Creative Orchestra - GEMINI Framework
   ============================================ */

/**
 * Theme configuration for each GEMINI work
 * Each theme includes:
 * - colors: Primary, secondary, accent colors
 * - gradients: Background and text gradients
 * - glows: Box shadow glow effects
 * - cssVariables: CSS custom property values
 */

export const workThemes = {
  /* ============================================
     FRAMEWORK
     ============================================ */

  gemini: {
    id: 'gemini',
    name: 'GEMINI',
    stage: 'Framework',
    colors: {
      primary: '#EDE9FE',      // Violet light
      secondary: '#DDD6FE',    // Violet medium
      accent: '#7C3AED',       // Violet
      accentLight: '#A78BFA',  // Light violet
      text: '#2E1065',         // Dark violet
      textMuted: '#5B21B6',    // Medium violet
    },
    gradients: {
      background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 50%, #C4B5FD 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #150a1f 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 50%, #7C3AED 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #A78BFA, #7C3AED 50%, #2E1065)',
      hero: 'linear-gradient(180deg, rgba(124, 58, 237, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(124, 58, 237, 0.3)',
      medium: '0 0 40px rgba(124, 58, 237, 0.4)',
      large: '0 0 60px rgba(124, 58, 237, 0.5)',
      text: '0 0 20px rgba(124, 58, 237, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#EDE9FE',
      '--theme-secondary': '#DDD6FE',
      '--theme-accent': '#7C3AED',
      '--theme-accent-rgb': '124, 58, 237',
      '--glow-color': 'rgba(124, 58, 237, 0.4)',
    },
  },

  /* ============================================
     GENERATION STAGE
     ============================================ */
  
  genie: {
    id: 'genie',
    name: 'GENIE',
    stage: 'Generation',
    colors: {
      primary: '#E8F4EA',      // Mint light
      secondary: '#D4EDDA',    // Mint medium
      accent: '#28A745',       // Green
      accentLight: '#5DD879',  // Light green
      text: '#1B4332',         // Dark green
      textMuted: '#2D6A4F',    // Medium green
    },
    gradients: {
      background: 'linear-gradient(135deg, #E8F4EA 0%, #D4EDDA 50%, #C1E4C9 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #0d1f12 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #28A745 0%, #5DD879 50%, #28A745 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #5DD879, #28A745 50%, #1B4332)',
      hero: 'linear-gradient(180deg, rgba(40, 167, 69, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(40, 167, 69, 0.3)',
      medium: '0 0 40px rgba(40, 167, 69, 0.4)',
      large: '0 0 60px rgba(40, 167, 69, 0.5)',
      text: '0 0 20px rgba(40, 167, 69, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#E8F4EA',
      '--theme-secondary': '#D4EDDA',
      '--theme-accent': '#28A745',
      '--theme-accent-rgb': '40, 167, 69',
      '--glow-color': 'rgba(40, 167, 69, 0.4)',
    },
  },

  midas: {
    id: 'midas',
    name: 'MIDAS',
    stage: 'Generation',
    colors: {
      primary: '#FFF9E6',      // Cream light
      secondary: '#FFF3CD',    // Cream medium
      accent: '#FFC107',       // Gold
      accentLight: '#FFD54F',  // Light gold
      text: '#5D4E37',         // Dark brown
      textMuted: '#8B7355',    // Medium brown
    },
    gradients: {
      background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CD 50%, #FFE9A0 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #1a1507 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #FFC107 0%, #FFD54F 50%, #FFC107 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #FFD54F, #FFC107 50%, #B8860B)',
      hero: 'linear-gradient(180deg, rgba(255, 193, 7, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(255, 193, 7, 0.3)',
      medium: '0 0 40px rgba(255, 193, 7, 0.4)',
      large: '0 0 60px rgba(255, 193, 7, 0.5)',
      text: '0 0 20px rgba(255, 193, 7, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#FFF9E6',
      '--theme-secondary': '#FFF3CD',
      '--theme-accent': '#FFC107',
      '--theme-accent-rgb': '255, 193, 7',
      '--glow-color': 'rgba(255, 193, 7, 0.4)',
    },
  },

  /* ============================================
     EVALUATION STAGE
     ============================================ */

  magics: {
    id: 'magics',
    name: 'MAGICS',
    stage: 'Evaluation',
    colors: {
      primary: '#E8F0FE',      // Sky light
      secondary: '#D4E5FC',    // Sky medium
      accent: '#4285F4',       // Blue
      accentLight: '#7BAAF7',  // Light blue
      text: '#1A365D',         // Dark blue
      textMuted: '#2C5282',    // Medium blue
    },
    gradients: {
      background: 'linear-gradient(135deg, #E8F0FE 0%, #D4E5FC 50%, #BBD4F8 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #0a1628 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #4285F4 0%, #7BAAF7 50%, #4285F4 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #7BAAF7, #4285F4 50%, #1A365D)',
      hero: 'linear-gradient(180deg, rgba(66, 133, 244, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(66, 133, 244, 0.3)',
      medium: '0 0 40px rgba(66, 133, 244, 0.4)',
      large: '0 0 60px rgba(66, 133, 244, 0.5)',
      text: '0 0 20px rgba(66, 133, 244, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#E8F0FE',
      '--theme-secondary': '#D4E5FC',
      '--theme-accent': '#4285F4',
      '--theme-accent-rgb': '66, 133, 244',
      '--glow-color': 'rgba(66, 133, 244, 0.4)',
    },
  },

  /* ============================================
     MANAGEMENT STAGE
     ============================================ */

  dimes: {
    id: 'dimes',
    name: 'DIMES',
    stage: 'Management',
    colors: {
      primary: '#F3E8FF',      // Lavender light
      secondary: '#E9D5FF',    // Lavender medium
      accent: '#9333EA',       // Purple
      accentLight: '#A855F7',  // Light purple
      text: '#4A1D6B',         // Dark purple
      textMuted: '#6B3FA0',    // Medium purple
    },
    gradients: {
      background: 'linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 50%, #DCC5F7 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #160a1f 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #9333EA 0%, #A855F7 50%, #9333EA 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #A855F7, #9333EA 50%, #4A1D6B)',
      hero: 'linear-gradient(180deg, rgba(147, 51, 234, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(147, 51, 234, 0.3)',
      medium: '0 0 40px rgba(147, 51, 234, 0.4)',
      large: '0 0 60px rgba(147, 51, 234, 0.5)',
      text: '0 0 20px rgba(147, 51, 234, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#F3E8FF',
      '--theme-secondary': '#E9D5FF',
      '--theme-accent': '#9333EA',
      '--theme-accent-rgb': '147, 51, 234',
      '--glow-color': 'rgba(147, 51, 234, 0.4)',
    },
  },

  aegis: {
    id: 'aegis',
    name: 'AEGIS',
    stage: 'Management',
    colors: {
      primary: '#FEE8E8',      // Blush light
      secondary: '#FECACA',    // Blush medium
      accent: '#EF4444',       // Red
      accentLight: '#F87171',  // Light red
      text: '#7F1D1D',         // Dark red
      textMuted: '#B91C1C',    // Medium red
    },
    gradients: {
      background: 'linear-gradient(135deg, #FEE8E8 0%, #FECACA 50%, #FCA5A5 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #1f0a0a 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #EF4444 0%, #F87171 50%, #EF4444 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #F87171, #EF4444 50%, #7F1D1D)',
      hero: 'linear-gradient(180deg, rgba(239, 68, 68, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(239, 68, 68, 0.3)',
      medium: '0 0 40px rgba(239, 68, 68, 0.4)',
      large: '0 0 60px rgba(239, 68, 68, 0.5)',
      text: '0 0 20px rgba(239, 68, 68, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#FEE8E8',
      '--theme-secondary': '#FECACA',
      '--theme-accent': '#EF4444',
      '--theme-accent-rgb': '239, 68, 68',
      '--glow-color': 'rgba(239, 68, 68, 0.4)',
    },
  },

  traces: {
    id: 'traces',
    name: 'TRACES',
    stage: 'Management',
    colors: {
      primary: '#E8F4F8',      // Sky light
      secondary: '#D1E9F1',    // Sky medium
      accent: '#0EA5E9',       // Sky blue
      accentLight: '#38BDF8',  // Light sky blue
      text: '#0C4A6E',         // Dark sky blue
      textMuted: '#075985',    // Medium sky blue
    },
    gradients: {
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D1E9F1 50%, #BAE6FD 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #0a1a24 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 50%, #0EA5E9 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #38BDF8, #0EA5E9 50%, #0C4A6E)',
      hero: 'linear-gradient(180deg, rgba(14, 165, 233, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(14, 165, 233, 0.3)',
      medium: '0 0 40px rgba(14, 165, 233, 0.4)',
      large: '0 0 60px rgba(14, 165, 233, 0.5)',
      text: '0 0 20px rgba(14, 165, 233, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#E8F4F8',
      '--theme-secondary': '#D1E9F1',
      '--theme-accent': '#0EA5E9',
      '--theme-accent-rgb': '14, 165, 233',
      '--glow-color': 'rgba(14, 165, 233, 0.4)',
    },
  },

  /* ============================================
     INSPIRATION STAGE
     ============================================ */

  euphoria: {
    id: 'euphoria',
    name: 'EUPHORIA',
    stage: 'INspiration',
    colors: {
      primary: '#E8FDF5',      // Aqua light
      secondary: '#D1FAE5',    // Aqua medium
      accent: '#10B981',       // Emerald
      accentLight: '#34D399',  // Light emerald
      text: '#064E3B',         // Dark emerald
      textMuted: '#047857',    // Medium emerald
    },
    gradients: {
      background: 'linear-gradient(135deg, #E8FDF5 0%, #D1FAE5 50%, #A7F3D0 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #0a1f17 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #10B981 0%, #34D399 50%, #10B981 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #34D399, #10B981 50%, #064E3B)',
      hero: 'linear-gradient(180deg, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(16, 185, 129, 0.3)',
      medium: '0 0 40px rgba(16, 185, 129, 0.4)',
      large: '0 0 60px rgba(16, 185, 129, 0.5)',
      text: '0 0 20px rgba(16, 185, 129, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#E8FDF5',
      '--theme-secondary': '#D1FAE5',
      '--theme-accent': '#10B981',
      '--theme-accent-rgb': '16, 185, 129',
      '--glow-color': 'rgba(16, 185, 129, 0.4)',
    },
  },

  retina: {
    id: 'retina',
    name: 'RETINA',
    stage: 'INspiration',
    colors: {
      primary: '#FEF3E8',      // Peach light
      secondary: '#FED7AA',    // Peach medium
      accent: '#F97316',       // Orange
      accentLight: '#FB923C',  // Light orange
      text: '#7C2D12',         // Dark orange
      textMuted: '#C2410C',    // Medium orange
    },
    gradients: {
      background: 'linear-gradient(135deg, #FEF3E8 0%, #FED7AA 50%, #FDBA74 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #1f140a 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #F97316 0%, #FB923C 50%, #F97316 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #FB923C, #F97316 50%, #7C2D12)',
      hero: 'linear-gradient(180deg, rgba(249, 115, 22, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(249, 115, 22, 0.3)',
      medium: '0 0 40px rgba(249, 115, 22, 0.4)',
      large: '0 0 60px rgba(249, 115, 22, 0.5)',
      text: '0 0 20px rgba(249, 115, 22, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#FEF3E8',
      '--theme-secondary': '#FED7AA',
      '--theme-accent': '#F97316',
      '--theme-accent-rgb': '249, 115, 22',
      '--glow-color': 'rgba(249, 115, 22, 0.4)',
    },
  },

  fenso: {
    id: 'fenso',
    name: 'FENSO',
    stage: 'INspiration',
    colors: {
      primary: '#F0F4FF',      // Ice light
      secondary: '#E0E7FF',    // Ice medium
      accent: '#6366F1',       // Indigo
      accentLight: '#818CF8',  // Light indigo
      text: '#312E81',         // Dark indigo
      textMuted: '#4338CA',    // Medium indigo
    },
    gradients: {
      background: 'linear-gradient(135deg, #F0F4FF 0%, #E0E7FF 50%, #C7D2FE 100%)',
      backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #0f0a1f 50%, #0a0a0f 100%)',
      text: 'linear-gradient(135deg, #6366F1 0%, #818CF8 50%, #6366F1 100%)',
      orb: 'radial-gradient(circle at 30% 30%, #818CF8, #6366F1 50%, #312E81)',
      hero: 'linear-gradient(180deg, rgba(99, 102, 241, 0.1) 0%, transparent 50%)',
    },
    glows: {
      small: '0 0 20px rgba(99, 102, 241, 0.3)',
      medium: '0 0 40px rgba(99, 102, 241, 0.4)',
      large: '0 0 60px rgba(99, 102, 241, 0.5)',
      text: '0 0 20px rgba(99, 102, 241, 0.5)',
    },
    cssVariables: {
      '--theme-primary': '#F0F4FF',
      '--theme-secondary': '#E0E7FF',
      '--theme-accent': '#6366F1',
      '--theme-accent-rgb': '99, 102, 241',
      '--glow-color': 'rgba(99, 102, 241, 0.4)',
    },
  },
};

/* ============================================
   DEFAULT & NOVEL THEMES
   ============================================ */

export const defaultTheme = {
  id: 'default',
  name: 'Default',
  colors: {
    primary: '#F8FAFF',
    secondary: '#E8E4F0',
    accent: '#818CF8',
    accentLight: '#A5B4FC',
    text: '#F8FAFC',
    textMuted: '#94A3B8',
  },
  gradients: {
    background: 'linear-gradient(135deg, #0a0a0f 0%, #0f1419 50%, #0a0a0f 100%)',
    backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #0f1419 50%, #0a0a0f 100%)',
    text: 'linear-gradient(135deg, #ffffff 0%, #a8d4ff 50%, #ffffff 100%)',
    orb: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4), rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05))',
    hero: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, transparent 50%)',
  },
  glows: {
    small: '0 0 20px rgba(255, 255, 255, 0.2)',
    medium: '0 0 40px rgba(255, 255, 255, 0.3)',
    large: '0 0 60px rgba(255, 255, 255, 0.4)',
    text: '0 0 20px rgba(255, 255, 255, 0.4)',
  },
  cssVariables: {
    '--theme-primary': '#F8FAFF',
    '--theme-secondary': '#E8E4F0',
    '--theme-accent': '#818CF8',
    '--theme-accent-rgb': '129, 140, 248',
    '--glow-color': 'rgba(255, 255, 255, 0.3)',
  },
};

export const novelTheme = {
  id: 'novel',
  name: 'Novel',
  colors: {
    primary: '#FDF6E3',      // Warm cream
    secondary: '#F5E6C8',
    accent: '#D4A574',       // Warm brown
    accentLight: '#E5C9A8',
    text: '#3D3D3D',
    textMuted: '#666666',
  },
  gradients: {
    background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1510 50%, #0a0a0f 100%)',
    backgroundDark: 'linear-gradient(135deg, #0a0a0f 0%, #1a1510 50%, #0a0a0f 100%)',
    text: 'linear-gradient(135deg, #FDF6E3 0%, #D4A574 50%, #FDF6E3 100%)',
    orb: 'radial-gradient(circle at 30% 30%, #FFD700, #D4A574 50%, #8B4513)',
    hero: 'linear-gradient(180deg, rgba(212, 165, 116, 0.1) 0%, transparent 50%)',
  },
  glows: {
    small: '0 0 20px rgba(212, 165, 116, 0.2)',
    medium: '0 0 40px rgba(212, 165, 116, 0.3)',
    large: '0 0 60px rgba(212, 165, 116, 0.4)',
    text: '0 0 20px rgba(212, 165, 116, 0.4)',
  },
  cssVariables: {
    '--theme-primary': '#FDF6E3',
    '--theme-secondary': '#F5E6C8',
    '--theme-accent': '#D4A574',
    '--theme-accent-rgb': '212, 165, 116',
    '--glow-color': 'rgba(212, 165, 116, 0.3)',
  },
};

/* ============================================
   HELPER FUNCTIONS
   ============================================ */

/**
 * Get theme configuration by work ID
 * @param {string} workId - Work identifier
 * @returns {Object} Theme configuration
 */
export const getWorkTheme = (workId) => {
  return workThemes[workId] || defaultTheme;
};

/**
 * Apply theme CSS variables to an element
 * @param {HTMLElement} element - Target element
 * @param {Object} theme - Theme configuration
 */
export const applyThemeVariables = (element, theme) => {
  if (!element || !theme?.cssVariables) return;
  
  Object.entries(theme.cssVariables).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
};

/**
 * Get all themes as an array
 * @returns {Array} Array of theme configurations
 */
export const getAllThemes = () => {
  return Object.values(workThemes);
};

/**
 * Get themes grouped by GEMINI stage
 * @returns {Object} Themes grouped by stage
 */
export const getThemesByStage = () => {
  return Object.values(workThemes).reduce((acc, theme) => {
    const stage = theme.stage;
    if (!acc[stage]) acc[stage] = [];
    acc[stage].push(theme);
    return acc;
  }, {});
};

/**
 * Generate CSS string for a theme
 * @param {Object} theme - Theme configuration
 * @returns {string} CSS variables string
 */
export const generateThemeCSS = (theme) => {
  if (!theme?.cssVariables) return '';
  
  return Object.entries(theme.cssVariables)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n');
};

export default workThemes;
